import { useRef } from 'react';
import { ClientOnly } from 'remix-utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';
import { VideoPlayer } from '@rocco/components/video-player/video-player';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import HoverButtonsImage from '@modules/page-builder/component/detail/detail-single-image-shell/HoverButtonsImage';
import { getImageAttrs } from '~/modules/images';
import { REVAMP_HERO_SIZES } from '../util/revamp-image-sizes';

/** The design's banner box, also the boost overlay's upscale target. */
const HERO_WIDTH = 1520;
const HERO_HEIGHT = 800;

export interface RevampHeroImageProps {
    imageUrl: string;
    /** The banner's own alt text. Empty when it has none — the h1 already names the article. */
    alt: string;
    caption?: string;
    /** Plays in place of the still when the editor uploaded a video banner. */
    videoUrl?: string;
    /** Autoplay the banner video — the legacy `?video=1` opt-in. */
    autoplay?: boolean;
    /** LibraryImage behind the banner; carries the boost and save targets. */
    libraryImageId?: number;
    /** The banner's LibraryItem, so a save lands on the right board pin. */
    libraryItemId?: number;
    wasUpscaled?: boolean;
    /** Original-resolution file, offered to staff who may download it. */
    downloadLink?: string;
}

/** Full-width hero banner with an offset caption, per the landscape/large module. */
export const RevampHeroImage = ({
    imageUrl,
    alt,
    caption,
    videoUrl,
    autoplay = false,
    libraryImageId,
    libraryItemId,
    wasUpscaled,
    downloadLink,
}: RevampHeroImageProps) => {
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    // Once per rendered player, not once per play — a pause/resume would
    // otherwise inflate the series.
    const isPlayEventTracked = useRef(false);

    // The same actions the body images carry, in the same top-right corner —
    // the banner is the article's largest image, so a reader who can save or
    // download the rest should not have to hunt for it here. Tagging is off:
    // the banner has no tagging surface on either article page.
    //
    // A video banner takes none of them: they would sit over the player's own
    // title bar and controls, and there is no still to pin, save or download.
    const hoverActions =
        !videoUrl && libraryImageId ? (
            <HoverButtonsImage
                photoID={libraryImageId}
                libraryItemId={libraryItemId}
                imageURL={imageUrl}
                downloadLink={downloadLink}
                taggingEnabled={false}
            />
        ) : null;

    // A video banner has no still to upscale, so boost is off there too, as it
    // is on the legacy banner.
    const canBoostBanner = !videoUrl && !mobile && !!libraryImageId;

    const bannerImage = (
        <img
            {...getImageAttrs(imageUrl, { sizes: REVAMP_HERO_SIZES })}
            // A caption is rendered below as a visible figcaption, which
            // already describes the image — repeating it here would read it
            // out twice.
            alt={caption ? '' : alt}
            // The design's ratio, also carried by the aspect class — the
            // attributes reserve the box before any CSS lands.
            width={HERO_WIDTH}
            height={HERO_HEIGHT}
            fetchPriority="high"
            decoding="async"
            className="aspect-[1520/800] w-full rounded-tile object-cover"
        />
    );

    // react-player is CJS, so it only survives the client bundle — the banner
    // image stands in until it mounts, the same guard the legacy video banner
    // and the guides page use.
    const media = videoUrl ? (
        <ClientOnly fallback={bannerImage}>
            {() => (
                <VideoPlayer
                    src={videoUrl}
                    // Only when the reader has to press play: the player takes
                    // any cover as react-player's `light` mode, which holds it
                    // on the poster until a click — so passing one here would
                    // stop `?video=1` from ever starting.
                    coverSrc={autoplay ? undefined : imageUrl}
                    fill
                    controls
                    playsInline
                    mode={autoplay ? 'auto' : 'default'}
                    onPlay={() => {
                        if (isPlayEventTracked.current) return;
                        isPlayEventTracked.current = true;
                        tracker.log('Video', {
                            url: new URL(window.location.href),
                        });
                    }}
                    // A failed embed leaves the cover in place rather than
                    // breaking the top of the article.
                    onError={() => {}}
                />
            )}
        </ClientOnly>
    ) : (
        bannerImage
    );

    // The actions stay hidden until the banner is hovered, the reveal the
    // legacy image shell drives from its own container rule.
    const frame = (
        <div className="relative aspect-[1520/800] w-full overflow-hidden rounded-tile [&:hover_.hover-buttons-image]:visible! [&:hover_.hover-buttons-image]:opacity-100!">
            {media}
            {hoverActions}
        </div>
    );

    return (
        <figure className="m-0 flex w-full flex-col gap-4 md:gap-6 md:px-[12.5rem]">
            {canBoostBanner && libraryImageId ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImageId}
                    currentImageUrl={imageUrl}
                    displayWidth={HERO_WIDTH}
                    displayHeight={HERO_HEIGHT}
                    wasUpscaled={!!wasUpscaled}
                    className="w-full"
                    // Boost keeps the bottom-right corner it holds on the body
                    // images and every other detail page; the hover actions own
                    // the top right, so the two never share a corner.
                >
                    {frame}
                </BoostResolutionOverlay>
            ) : (
                frame
            )}
            {caption && (
                <figcaption className="px-4.5 font-sans text-[0.75rem] leading-[1.3] tracking-[-0.03em] text-black/80 md:w-[25rem] md:px-0 md:text-[0.87rem]">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
};
